import React, { useState, useEffect } from 'react';
import { Modal, message, Button } from 'antd';
import { useMediaQuery } from 'react-responsive';
import { UploadOutlined, FilterOutlined } from '@ant-design/icons';
import { FileText, Upload, ChevronUp, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { pdfjs } from 'react-pdf';
import { useCompany } from '../../contexts/CompanyContext';

// Hooks
import { useDocuments } from './hooks/useDocuments';
import { useDocumentPreview } from './hooks/useDocumentPreview';
import { useDocumentUpload } from './hooks/useDocumentUpload';
import { useDocumentFiltering } from './hooks/useDocumentFiltering';
import { useEmailDocument } from './hooks/useEmailDocument';
import { useEditDocument } from './hooks/useEditDocument';

// Components
import DocumentTable from './components/DocumentTable';
import DocumentFilters from './components/DocumentFilters';
import UploadModal from './components/UploadModal';
import EmailModal from './components/EmailModal';
import EditModal from './components/EditModal';
import PreviewModal from './components/PreviewModal';

// Styles and constants
import {
  PageContainer,
  GlassCard,
  ContentWrapper,
  UploadCard,
  TableWrapper,
  GlobalStyle,
} from './constants/styles';

// Utils
import { isDateInRange, matchesGlobalSearch } from './utils/dateUtils';
import { fetchClientNames } from '../../api/planDocumentsApi';

// Configure pdfjs
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const PlanDocuments = () => {
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtersCollapsed, setFiltersCollapsed] = useState(false);
  const { company, isCustomerView } = useCompany();

  // Initialize hooks
  const {
    documents,
    error,
    fetchDocuments,
    uploadDocument,
    deleteDocument,
    downloadDocument,
    updateDocumentMetadata,
  } = useDocuments();

  const {
    file,
    uploadFileName,
    setUploadFileName,
    isFileUploaded,
    isUploadModalVisible,
    uploadCompany,
    setUploadCompany,
    uploadType,
    setUploadType,
    handleFileChange,
    showUploadModal,
    handleUploadModalCancel,
    handleUploadModalOk,
  } = useDocumentUpload(uploadDocument);

  const {
    searchTerm,
    setSearchTerm,
    globalSearch,
    setGlobalSearch,
    selectedColumns,
    setSelectedColumns,
    sortedInfo,
    filteredInfo,
    setFilteredInfo,
    dateFilter,
    setDateFilter,
    dateRangeFilter,
    setDateRangeFilter,
    filteredDocuments,
    handleChange,
    clearAllFilters,
  } = useDocumentFiltering(documents);

  const {
    selectedDocument,
    isPreviewModalVisible,
    pdfFile,
    chatMessages,
    currentPage,
    isStreaming,
    setCurrentPage,
    handlePreview,
    handleSendMessage,
    handleClearChat,
    handlePreviewModalCancel,
  } = useDocumentPreview();

  const {
    isEmailModalVisible,
    selectedEmailDocument,
    recipientEmail,
    setRecipientEmail,
    recipientName,
    setRecipientName,
    emailSubject,
    setEmailSubject,
    emailMessage,
    setEmailMessage,
    sending,
    handleEmailDocument,
    handleEmailModalCancel,
    handleSendEmail,
  } = useEmailDocument();

  const {
    editingDocument,
    isEditModalVisible,
    setIsEditModalVisible,
    editCompany,
    setEditCompany,
    editType,
    setEditType,
    editFileName,
    setEditFileName,
    handleMetadataUpdate,
    handleEditModalOk,
  } = useEditDocument(fetchDocuments);

  // Fetch companies on mount
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        setLoading(true);
        const currentYear = new Date().getFullYear();
        const clientNames = await fetchClientNames(currentYear);
        setCompanies(clientNames);
      } catch (err) {
        console.error('Error fetching companies:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCompanies();
  }, []);

  // Handle delete with confirmation
  const handleDelete = (documentName) => {
    Modal.confirm({
      title: 'Are you sure you want to delete this file?',
      content: documentName,
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        await deleteDocument(documentName);
      },
    });
  };

  // Filter documents based on all criteria
  const getFilteredDocuments = () => {
    return documents.filter((doc) => {
      // First, filter by company if user is a customer
      if (isCustomerView && company) {
        if (doc.company !== company) {
          return false;
        }
      }
      
      const matchesGlobal = matchesGlobalSearch(doc, globalSearch);
      const matchesNameSearch = doc.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCompanyFilter = !filteredInfo.company?.length || 
        filteredInfo.company.includes(doc.company);
      const matchesTypeFilter = !filteredInfo.type?.length || 
        filteredInfo.type.includes(doc.type);
      const matchesDateFilter = isDateInRange(
        doc.uploadDate,
        dateFilter?.toDate(),
        dateRangeFilter?.map(d => d?.toDate())
      );
      
      return matchesGlobal && 
             matchesNameSearch && 
             matchesCompanyFilter && 
             matchesTypeFilter &&
             matchesDateFilter;
    });
  };

  const hasActiveFilters = Object.keys(filteredInfo).some(key => 
    filteredInfo[key] && (Array.isArray(filteredInfo[key]) ? filteredInfo[key].length > 0 : true)
  ) || searchTerm !== '' || dateFilter !== null || dateRangeFilter !== null || globalSearch !== '';

  const customSelectStyles = `
    .custom-select .ant-select-selector {
      height: 36px !important;
      border-radius: 8px !important;
      border-color: #e5e7eb !important;
      border-width: 1px !important;
    }
    
    .custom-select .ant-select-selection-search-input {
      height: 34px !important;
    }
    
    .custom-select .ant-select-selection-placeholder,
    .custom-select .ant-select-selection-item {
      line-height: 34px !important;
    }
  `;

  return (
    <PageContainer>
      <GlassCard>
        <ContentWrapper>
          <UploadCard style={{ 
            marginBottom: '1rem',
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(226, 232, 240, 0.5)',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.04)',
          }}>
            {/* Modern header with title, stats, and upload button */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: filtersCollapsed ? 0 : '1.5rem',
              flexWrap: 'wrap',
              gap: '1rem',
              transition: 'margin-bottom 0.3s ease'
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: isMobile ? '1rem' : '2.5rem', 
                flex: 1,
                flexWrap: isMobile ? 'wrap' : 'nowrap'
              }}>
                <h2 style={{ 
                  fontSize: isMobile ? '1.5rem' : '1.875rem', 
                  fontWeight: '800',
                  margin: 0,
                  background: 'linear-gradient(135deg, #1e293b 0%, #475569 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  letterSpacing: '-0.025em'
                }}>
                  Document Library
                </h2>
                
                {/* Modern stats pills */}
                {!isMobile && (
                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '0.5rem',
                      padding: '0.5rem 1rem',
                      background: 'rgba(59, 130, 246, 0.08)',
                      borderRadius: '9999px',
                      border: '1px solid rgba(59, 130, 246, 0.2)'
                    }}>
                      <FileText size={16} style={{ color: '#3b82f6' }} />
                      <span style={{ fontSize: '0.875rem', color: '#1e293b', fontWeight: 600 }}>
                        {documents.length} total
                      </span>
                    </div>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '0.5rem',
                      padding: '0.5rem 1rem',
                      background: 'rgba(16, 185, 129, 0.08)',
                      borderRadius: '9999px',
                      border: '1px solid rgba(16, 185, 129, 0.2)'
                    }}>
                      <Upload size={16} style={{ color: '#10b981' }} />
                      <span style={{ fontSize: '0.875rem', color: '#1e293b', fontWeight: 600 }}>
                        {documents.filter(doc => 
                          new Date(doc.uploadDate).toDateString() === new Date().toDateString()
                        ).length} today
                      </span>
                    </div>
                  </div>
                )}
              </div>
              
              <div style={{ display: 'flex', gap: '0.75rem', width: isMobile ? '100%' : 'auto' }}>
                <motion.button
                  whileHover={{ scale: 1.02, y: -1 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setFiltersCollapsed(!filtersCollapsed)}
                  className="group"
                  style={{
                    background: 'linear-gradient(135deg, #10b981 0%, #10b981 50%, #059669 100%)',
                    border: 'none',
                    height: isMobile ? '40px' : '44px',
                    borderRadius: '12px',
                    fontWeight: 600,
                    fontSize: isMobile ? '0.875rem' : '0.9375rem',
                    padding: isMobile ? '0 1rem' : '0 1.5rem',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    transition: 'all 0.2s ease',
                    flex: isMobile ? 1 : 'initial',
                    minWidth: isMobile ? 'auto' : '140px',
                    boxShadow: '0 4px 14px 0 rgba(16, 185, 129, 0.3)',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 6px 20px 0 rgba(16, 185, 129, 0.4)';
                    e.currentTarget.style.filter = 'brightness(1.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = '0 4px 14px 0 rgba(16, 185, 129, 0.3)';
                    e.currentTarget.style.filter = 'brightness(1)';
                  }}
                >
                  <motion.div
                    animate={{ rotate: filtersCollapsed ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    {filtersCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                  </motion.div>
                  <span>{filtersCollapsed ? 'Show' : 'Hide'} Filters</span>
                  {!isMobile && (
                    <span style={{
                      fontSize: '0.75rem',
                      background: 'rgba(255, 255, 255, 0.2)',
                      padding: '0.125rem 0.375rem',
                      borderRadius: '0.375rem',
                      fontFamily: 'monospace',
                    }}>
                      ⌘F
                    </span>
                  )}
                </motion.button>
                
                {!isCustomerView && (
                  <Button
                    type="primary"
                    icon={<UploadOutlined />}
                    onClick={showUploadModal}
                    size={isMobile ? 'middle' : 'large'}
                    style={{
                    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                    border: 'none',
                    height: isMobile ? '40px' : '44px',
                    borderRadius: '12px',
                    fontWeight: 600,
                    fontSize: isMobile ? '0.875rem' : '0.9375rem',
                    padding: isMobile ? '0 1rem' : '0 1.5rem',
                    boxShadow: '0 4px 14px 0 rgba(59, 130, 246, 0.3)',
                    flex: isMobile ? 1 : 'initial',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 6px 20px 0 rgba(59, 130, 246, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 14px 0 rgba(59, 130, 246, 0.3)';
                  }}
                >
                  {isMobile ? 'Upload' : 'Upload New Document'}
                </Button>
                )}
              </div>
            </div>

            {/* Compact filters with smooth animation */}
            <AnimatePresence initial={false}>
              {!filtersCollapsed && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ 
                    duration: 0.3,
                    ease: [0.25, 0.46, 0.45, 0.94],
                    opacity: { duration: 0.2 }
                  }}
                  style={{ overflow: 'hidden' }}
                >
                  <div style={{ paddingTop: '1rem' }}>
                    <DocumentFilters
                      documents={documents}
                      companies={companies}
                      globalSearch={globalSearch}
                      setGlobalSearch={setGlobalSearch}
                      searchTerm={searchTerm}
                      setSearchTerm={setSearchTerm}
                      filteredInfo={filteredInfo}
                      setFilteredInfo={setFilteredInfo}
                      dateFilter={dateFilter}
                      setDateFilter={setDateFilter}
                      dateRangeFilter={dateRangeFilter}
                      setDateRangeFilter={setDateRangeFilter}
                      hasActiveFilters={hasActiveFilters}
                    />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </UploadCard>

          <TableWrapper>
            <DocumentTable
              documents={documents}
              filteredDocuments={getFilteredDocuments()}
              sortedInfo={sortedInfo}
              handleChange={handleChange}
              handlePreview={handlePreview}
              handleDownload={downloadDocument}
              handleDelete={handleDelete}
              handleEmailDocument={handleEmailDocument}
              handleMetadataUpdate={handleMetadataUpdate}
              loading={loading}
              error={error}
              isMobile={isMobile}
            />
          </TableWrapper>
        </ContentWrapper>
      </GlassCard>

      <UploadModal
        isUploadModalVisible={isUploadModalVisible}
        handleUploadModalOk={handleUploadModalOk}
        handleUploadModalCancel={handleUploadModalCancel}
        file={file}
        handleFileChange={handleFileChange}
        uploadCompany={uploadCompany}
        setUploadCompany={setUploadCompany}
        uploadType={uploadType}
        setUploadType={setUploadType}
        isFileUploaded={isFileUploaded}
        companies={companies}
      />

      <PreviewModal
        isPreviewModalVisible={isPreviewModalVisible}
        handlePreviewModalCancel={handlePreviewModalCancel}
        selectedDocument={selectedDocument}
        pdfFile={pdfFile}
        chatMessages={chatMessages}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        handleSendMessage={handleSendMessage}
        handleClearChat={handleClearChat}
        isMobile={isMobile}
        isStreaming={isStreaming}
      />

      <EmailModal
        isEmailModalVisible={isEmailModalVisible}
        handleEmailModalCancel={handleEmailModalCancel}
        handleSendEmail={handleSendEmail}
        selectedEmailDocument={selectedEmailDocument}
        recipientName={recipientName}
        setRecipientName={setRecipientName}
        recipientEmail={recipientEmail}
        setRecipientEmail={setRecipientEmail}
        emailSubject={emailSubject}
        setEmailSubject={setEmailSubject}
        emailMessage={emailMessage}
        setEmailMessage={setEmailMessage}
        sending={sending}
      />

      <EditModal
        isEditModalVisible={isEditModalVisible}
        handleEditModalOk={handleEditModalOk}
        setIsEditModalVisible={setIsEditModalVisible}
        editFileName={editFileName}
        setEditFileName={setEditFileName}
        editCompany={editCompany}
        setEditCompany={setEditCompany}
        editType={editType}
        setEditType={setEditType}
        companies={companies}
      />

      <style>{customSelectStyles}</style>
      <GlobalStyle />
    </PageContainer>
  );
};

export default PlanDocuments;